import React, { Fragment, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Grid, Paper } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Dialog } from '@progress/kendo-react-dialogs'
import { Notification, NotificationGroup } from '@progress/kendo-react-notification'
import { Fade } from '@progress/kendo-react-animation'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

// import userflow from 'userflow.js'

import { DialogMode } from '../../constants'

import MainLayout from 'layouts/mainLayout'
import BusinessProfileCard from 'components/LandingPage/BusinessProfileCard'
import BusinessCard from 'components/LandingPage/BusinessInformation'

import VaultCard from 'components/LandingPage/VaultCard'
import CashFlowCard from 'components/LandingPage/CashFlowCard'
import SalesCard from 'components/LandingPage/SalesCard'
import NavMain from 'components/Nav/NavMain'

import ErrorMessage from 'components/Common/ErrorMessage'
// import IdleTimer from 'components/Common/IdleTimer'

import FinancialOverviewDialog from 'components/Common/Modal/FinancialOverviewDialog'
import SalesDialog from 'components/Common/Modal/SalesDialog'
import SubscriptionModal from 'components/Common/Modal/SubscriptionModal'
import ConnectAppModal from 'components/Common/Modal/ConnectAppModal'

// import ModalTitle from 'components/Common/ModalTitle'

import api from 'api'
import actionTypes from 'store/actions'
import ZendeskLink from 'components/Common/ZendeskLink'
const stripePromise = loadStripe(process.env['REACT_APP_STRIPE_PUBLIC_KEY'])

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}))

function LandingPage(props) {
  const dialogMode = useSelector(state => state.appReducer.dialogMode)
  const errorNotification = useSelector(state => state.appReducer.errorNotification)
  const companyId = useSelector(state => state.apiResponseReducers.companyId)
  const errorState = useSelector(state => state.appReducer.errorState)
  const dayspanSelected = useSelector(state => state.appReducer.financialOverviewDaySpan)
  const isModalStepOpen = useSelector(state => state.appReducer.isOpenSubscriptionModal)
  const isCompanyNameModalOpen = useSelector(state => state.appReducer.isOpenCompanyNameModal)
  const user = useSelector(state => state.userReducer.user)
  const dispatch = useDispatch()

  const modalHandler = useCallback(
    value => {
      dispatch({ type: actionTypes.TOGGLESUBSCRIPTIONMODAL, data: value })
      dispatch({ type: actionTypes.TOGGLECOMPANYNAMEMODAL, data: value })
    },
    [dispatch],
  )

  useEffect(() => {
    if (!user) return
    const dashboardData = async () => {
      //Todo: Sending dummy company ID untill the feature is implemented
      await api.getDashboardData(companyId, dayspanSelected, 6)
    }

    const identifyStonlyUser = () => {
      // await api.identifyStonlyUser(user.id, `${user.firstName} ${user.lastName}`, user.email, new Date(user.accessPeriodEnd).toISOString())
      window.StonlyWidget('identify', user.id, {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      })
    }

    // userflow.identify(user.id, {
    //   name: `${user.firstName} ${user.lastName}`,
    //   email: user.email,
    //   signed_up_at: new Date(user.accessPeriodEnd).toISOString(),
    // })
    dashboardData()
    identifyStonlyUser()
    // eslint-disable-next-line
  }, [user, companyId])

  const onErrorMessageCloseClick = () => {
    dispatch({ type: actionTypes.SETERRORSTATE, data: false })
  }

  const tiles = [
    {
      id: 1,
      height: {
        md: '300px',
        lg: '300px',
      },
      span: {
        xs: 12,
        sm: 12,
        md: 5,
        lg: 3,
      },
      tile: <BusinessCard />,
    },
    {
      id: 2,
      height: {
        md: '300px',
        lg: '300px',
      },
      span: {
        xs: 12,
        sm: 12,
        md: 6,
        lg: 6,
      },
      tile: <BusinessProfileCard />,
    },

    {
      id: 3,
      height: {
        md: '550px',
        lg: '300px',
      },
      span: {
        xs: 12,
        sm: 12,
        md: 5,
        lg: 3,
      },
      tile: <VaultCard />,
    },
    {
      id: 4,
      height: {
        xs: '480px',
        sm: '480px',
        md: '550px',
        lg: '550px',
      },
      span: {
        xs: 12,
        sm: 12,
        md: 7,
        lg: 6,
      },
      tile: <CashFlowCard />,
    },
    {
      id: 5,
      height: {
        md: '480px',
        lg: '550px',
      },
      span: {
        xs: 12,
        sm: 12,
        md: 6,
        lg: 3,
      },
      tile: <SalesCard />,
    },
    {
      id: 6,
      height: {
        md: '480px',
        lg: '550px',
      },
      span: {
        xs: 12,
        sm: 12,
        md: 6,
        lg: 3,
      },
      tile: <NavMain showWidget={true} />,
    },
  ]

  return (
    <MainLayout>
      {/* <IdleTimer maxIdleTime={process.env['REACT_APP_MAX_IDLE_TIME_DASHBOARD']} /> */}
      {errorState && <ErrorMessage error={'Something went wrong'} onCloseClick={onErrorMessageCloseClick} />}
      {!errorState && (
        <Fragment>
          <Grid
            container
            spacing={{
              xs: 1,
              sm: 2,
              md: 2,
              lg: 3,
              xl: 4,
            }}
          >
            {tiles.map((tile, index) => (
              <Grid key={tile.id} item xs={tile.span.xs} sm={tile.span.sm} md={tile.span.md} lg={tile.span.lg}>
                <Item
                  sx={{
                    height: tile.height,
                    borderRadius: 3,
                    padding: 0,
                    boxShadow: '4px 4px 15px rgba(10, 120, 205, 0.08)',
                  }}
                >
                  {tile.tile}
                </Item>
              </Grid>
            ))}
          </Grid>
          {dialogMode === DialogMode.FINANCE && (
            <Dialog
              height={'85%'}
              width={'80%'}
            // title={
            //   <ModalTitle
            //     title={'Financial Overview'}
            //     titleImg="https://dashboard.app.markaaz.com/static/media/markaaz_logo_blue.043fce4d.png"
            //   />
            // }
            >
              <FinancialOverviewDialog />
            </Dialog>
          )}
          {dialogMode === DialogMode.SALES && (
            <Dialog
              height={'85%'}
              width={'80%'}
            // title={
            //   <ModalTitle
            //     title={'Financial Overview'}
            //     titleImg="https://dashboard.app.markaaz.com/static/media/markaaz_logo_blue.043fce4d.png"
            //   />
            // }
            >
              <SalesDialog />
            </Dialog>
          )}

          <NotificationGroup
            style={{
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <Fade enter={true} exit={true}>
              {errorNotification && (
                <Notification type={{ style: 'error', icon: true }} closable={false}>
                  <span>Oops!! Something went wrong.</span>
                </Notification>
              )}
            </Fade>
          </NotificationGroup>
          <ConnectAppModal isModalOpen={isCompanyNameModalOpen} setModalOpen={modalHandler} />
          <Elements stripe={stripePromise}>
            <SubscriptionModal isModalOpen={isModalStepOpen} setModalOpen={modalHandler} />
          </Elements>
        </Fragment>
      )}
      <ZendeskLink />
    </MainLayout>
  )
}

export default LandingPage
